import { createContext, useEffect, useReducer, useCallback } from "react";

import {
  ActionMapType,
  AuthStateType,
  AuthUserType,
  JWTContextType,
} from "./types";
import { getCurrentLocation, getLocation } from "../utils/common";
import fetcher from "../api/fetcher";
import { END_POINTS } from "../api/EndPoints";

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = "INITIAL",
  LOGIN = "LOGIN",
  REGISTER = "REGISTER",
  LOGOUT = "LOGOUT",
}

type Payload = {
  [Types.LOGIN]: {
    isAffiliate: boolean;
    isSalesUser: boolean;
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  isAffiliate: false,
  isSalesUser: false,
  isInitialized: false,
  isAuthenticated: false,
  user: {} as AuthUserType,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isAffiliate: action.payload.isAffiliate,
      isSalesUser: action.payload.isSalesUser,
      isInitialized: true,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isInitialized: true,
      isAffiliate: false,
      isSalesUser: false,
      isAuthenticated: false,
      user: {} as AuthStateType["user"],
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken =
        typeof window !== "undefined" ? sessionStorage.getItem("ap_token") : "";
      if (accessToken) {
        const response = await fetcher.get(END_POINTS.AUTH.USER);
        const { data: user } = response.data;

        dispatch({
          type: Types.LOGIN,
          payload: {
            user,
            isAffiliate: user.isAffiliate || false,
            isSalesUser: user.userType === "salesUser" || false,
          },
        });
      } else {
        logout();
      }
    } catch (error) {
      logout();
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGOUT
  const logout = async () => {
    sessionStorage.removeItem("ap_token");
    sessionStorage.removeItem("ap_user");
    dispatch({
      type: Types.LOGOUT,
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        initialize,
        loginWithGoogle: () => {},
        loginWithGithub: () => {},
        loginWithTwitter: () => {},
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
